import React, { useState, useRef } from 'react';
import { Modal, Button, Form, ProgressBar, Table, Alert } from 'react-bootstrap';
import { LuCloudUpload, LuDownload, LuFileSpreadsheet, LuCircleCheck, LuCircleX } from 'react-icons/lu';
import axios from 'axios';
import { toast } from 'react-toastify';

const baseURL = process.env.REACT_APP_BASEURL;

const INITIAL_STATE = {
    show: false,
    file: null,
    uploading: false,
    progress: 0,
    result: null,
    error: null,
};

export default function BulkCustomerUploadModal({ onUploadComplete }) {
    const [state, setState] = useState(INITIAL_STATE);
    const fileInputRef = useRef(null);

    const token = localStorage.getItem('token');

    // --- Download CSV Template ---
    const handleDownloadTemplate = async () => {
        try {
            const response = await axios.get(`${baseURL}/customer/bulk/template`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'customer_upload_template.csv');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (err) {
            toast.error('Failed to download template');
        }
    };

    // --- File Selection ---
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.csv')) {
            setState(s => ({ ...s, error: 'Please select a valid .csv file.' }));
            return;
        }
        setState(s => ({ ...s, file, error: null, result: null }));
    };

    // --- Upload with Progress ---
    const handleUpload = async () => {
        if (!state.file) {
            setState(s => ({ ...s, error: 'Please select a CSV file first.' }));
            return;
        }

        const formData = new FormData();
        formData.append('file', state.file);

        setState(s => ({ ...s, uploading: true, progress: 0, result: null, error: null }));

        try {
            const response = await axios.post(`${baseURL}/customer/bulk/upload`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const percent = progressEvent.total
                        ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
                        : 50;
                    setState(s => ({ ...s, progress: percent }));
                },
            });

            const result = response.data.data;
            setState(s => ({ ...s, uploading: false, progress: 100, result }));

            if (result.summary.inserted > 0) {
                toast.success(`${result.summary.inserted} customers uploaded successfully!`);
                if (onUploadComplete) onUploadComplete();
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Upload failed. Please try again.';
            setState(s => ({ ...s, uploading: false, progress: 0, error: msg }));
            toast.error(msg);
        }
    };

    const handleClose = () => {
        setState(INITIAL_STATE);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const { show, file, uploading, progress, result, error } = state;

    return (
        <>
            {/* Trigger Button */}
            <Button
                variant="outline-primary"
                onClick={() => setState(s => ({ ...s, show: true }))}
                className="d-flex align-items-center gap-2 rounded-pill px-4 py-2"
            >
                <LuCloudUpload size={18} /> Bulk Upload
            </Button>

            {/* Modal */}
            <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold d-flex align-items-center gap-2">
                        <LuFileSpreadsheet size={24} className="text-primary" />
                        Bulk Customer Upload
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="px-4 pb-4">
                    {/* Step 1: Download Template */}
                    <div className="p-4 rounded-4 bg-light mb-4">
                        <h6 className="fw-bold mb-2">Step 1 — Download Template</h6>
                        <p className="text-muted small mb-3">
                            Download the pre-formatted CSV template, fill in your customers, then upload it below.
                            Required field: <code>name</code>. Address and email are optional.
                        </p>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="rounded-pill px-4 d-flex align-items-center gap-2"
                            onClick={handleDownloadTemplate}
                        >
                            <LuDownload size={16} /> Download CSV Template
                        </Button>
                    </div>

                    {/* Step 2: Upload */}
                    <div className="p-4 rounded-4 border mb-4">
                        <h6 className="fw-bold mb-3">Step 2 — Upload Your CSV</h6>

                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold text-muted text-uppercase">
                                Select File (.csv only, max 500 rows, max 5MB)
                            </Form.Label>
                            <Form.Control
                                type="file"
                                accept=".csv"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                className="py-2"
                                disabled={uploading}
                            />
                            {file && (
                                <Form.Text className="text-success mt-1 d-block">
                                    <LuCircleCheck className="me-1" />
                                    {file.name} ({(file.size / 1024).toFixed(1)} KB) selected
                                </Form.Text>
                            )}
                        </Form.Group>

                        {error && (
                            <Alert variant="danger" className="py-2 small">
                                <LuCircleX className="me-2" />{error}
                            </Alert>
                        )}

                        {uploading && (
                            <ProgressBar
                                animated
                                now={progress}
                                label={`${progress}%`}
                                className="mb-3 rounded-pill"
                                variant="primary"
                            />
                        )}

                        <Button
                            variant="primary"
                            className="w-100 rounded-pill py-2 fw-bold"
                            onClick={handleUpload}
                            disabled={uploading || !file}
                        >
                            {uploading
                                ? `Uploading... ${progress}%`
                                : <><LuCloudUpload size={18} className="me-2" />Upload Customers</>
                            }
                        </Button>
                    </div>

                    {/* Step 3: Results */}
                    {result && (
                        <div className="p-4 rounded-4 border">
                            <h6 className="fw-bold mb-4">Upload Results</h6>

                            {/* Summary Cards */}
                            <div className="row g-3 mb-4">
                                <div className="col-4">
                                    <div className="text-center p-3 rounded-4 bg-light">
                                        <h4 className="fw-black mb-0">{result.summary.total}</h4>
                                        <small className="text-muted fw-bold text-uppercase">Total Rows</small>
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div className="text-center p-3 rounded-4" style={{ background: '#e8f5e9' }}>
                                        <h4 className="fw-black mb-0 text-success">{result.summary.inserted}</h4>
                                        <small className="text-muted fw-bold text-uppercase">Inserted</small>
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div className="text-center p-3 rounded-4" style={{ background: result.summary.failed > 0 ? '#ffebee' : '#e8f5e9' }}>
                                        <h4 className={`fw-black mb-0 ${result.summary.failed > 0 ? 'text-danger' : 'text-success'}`}>
                                            {result.summary.failed}
                                        </h4>
                                        <small className="text-muted fw-bold text-uppercase">Failed</small>
                                    </div>
                                </div>
                            </div>

                            {/* Failure Table */}
                            {result.failures?.length > 0 && (
                                <div>
                                    <h6 className="small fw-bold text-danger text-uppercase mb-2 d-flex align-items-center gap-2">
                                        <LuCircleX size={14} /> Failed Rows
                                    </h6>
                                    <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                                        <Table size="sm" className="border rounded-4 overflow-hidden small">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Row</th>
                                                    <th>Name</th>
                                                    <th>Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {result.failures.map((f, i) => (
                                                    <tr key={i}>
                                                        <td className="text-muted">#{f.row}</td>
                                                        <td className="fw-medium">{f.name || '—'}</td>
                                                        <td className="text-danger">{f.reason}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
}
